import {
  Box,
  Button,
  Divider,
  DropdownButton,
  EmptyState,
  FlexBox,
  IconButton,
  IconNames,
  InputText,
  SelectionList,
  SelectionListChangeEvent,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { QueueDashboardQueueType } from 'lib-dashboard-types';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import {
  ChangeEvent,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounce, useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { queuesApi } from 'apis/queues';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { useServerStorage } from 'utils/useServerStorage';

import styles from './queueDashboardQueuesControl.module.scss';

export const QueueDashboardQueuesControl = memo(
  function QueueDashboardQueuesControl(): JSX.Element {
    const { params, patchParams } = useAppSearchParams('/dashboard/queues');
    const storage = useServerStorage('user');

    const hasAnalyticsAccess = useHasAccess('analytics', {
      fallbackRole: 'teamlead',
    });

    const dropdownRef = useRef<HTMLDivElement>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    const {
      asyncStatus,
      items: queues,
      changeArg,
      search,
      hasNextPage,
      nextPage,
    } = useLazyPaginatedEndpoint({
      endpoint: queuesApi.endpoints.readQueues,
      itemsSelector: ({ data }) => data.queues,
      infinite: true,
    });

    useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

    useUpdateEffect(() => {
      search(debouncedSearchTerm);

      if (debouncedSearchTerm) {
        ampli.dashboardQueuesSearchFieldSubmitted({ location: 'dropdown' });
      } else {
        ampli.dashboardQueuesSearchFieldCleared({ location: 'dropdown' });
      }
    }, [changeArg, debouncedSearchTerm]);

    const handleToggle = useCallback(
      (newIsOpen: boolean) => {
        if (!hasAnalyticsAccess) return;

        if (newIsOpen && !queues.length) {
          changeArg(
            {
              searchTerm,
              sort: {
                key: 'name',
              },
              isArchived: false,
            },
            { debounceTime: 0 },
          );
        } else {
          if (dropdownRef.current) {
            dropdownRef.current.scrollTop = 0;
          }
        }
      },
      [hasAnalyticsAccess, changeArg, queues.length, searchTerm],
    );

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    };

    const handleClearSearch = () => {
      setSearchTerm('');
    };

    const handleChangeQueueType = useCallback(
      (type: QueueDashboardQueueType) => {
        patchParams({
          set: { type },
          unset: ['queue', 'limit', 'offset'],
        });

        storage.set({
          dashboard: (draft) => {
            draft.defaultQueueType = type;
          },
        });

        ampli.dashboardQueuesTypeChanged({
          type,
          location: 'dropdown',
        });
      },
      [patchParams, storage],
    );

    const handleSelectQueues = useCallback(
      ({ selectedValues }: SelectionListChangeEvent<string>) => {
        if (selectedValues.length) {
          patchParams({
            set: {
              type: 'custom',
              queue: selectedValues,
            },
            unset: ['limit', 'offset'],
          });

          ampli.dashboardQueuesTypeChanged({
            type: 'custom',
            location: 'dropdown',
          });

          ampli.dashboardQueuesManuallySelected({
            count: selectedValues.length,
          });
        } else {
          const nextQueueType =
            storage.data.dashboard?.defaultQueueType ?? 'agent';

          patchParams({
            set: {
              type: nextQueueType,
            },
            unset: ['queue', 'limit', 'offset'],
          });

          ampli.dashboardQueuesTypeChanged({
            type: nextQueueType,
            location: 'dropdown',
          });
        }
      },
      [patchParams, storage.data.dashboard?.defaultQueueType],
    );

    const selectedQueues = useMemo(
      (): string[] =>
        params.type === 'custom' && params.queue ? [params.queue].flat() : [],
      [params],
    );

    const label = useMemo(() => {
      if (params.type === 'custom') {
        return `${selectedQueues.length} ${
          selectedQueues.length === 1 ? 'queue' : 'queues'
        }`;
      }

      return queueTypePresets[params.type ?? 'agent'].label;
    }, [params, selectedQueues.length]);

    return (
      <DropdownButton
        dropdown={
          <div className={styles.dropdown} ref={dropdownRef}>
            <Box
              className={styles.section}
              padding={hasAnalyticsAccess ? '300 300 0' : '300'}
            >
              <Divider>
                <Text.Paragraph as="span" color="tertiary">
                  Quick filters
                </Text.Paragraph>
              </Divider>

              <Box className={styles.presets}>
                {Object.entries(queueTypePresets).map(
                  ([type, { label, icon }]) => {
                    const isSelected = type === params.type;

                    return (
                      <Button
                        key={type}
                        variant={isSelected ? 'primary' : 'secondary'}
                        icon={icon}
                        onClick={() => {
                          if (!isSelected) {
                            handleChangeQueueType(
                              type as QueueDashboardQueueType,
                            );
                          }
                          handleToggle(false);
                        }}
                      >
                        {label}
                      </Button>
                    );
                  },
                )}
              </Box>
            </Box>

            {hasAnalyticsAccess && (
              <>
                <Box
                  className={classNames(styles.section, styles.sticky)}
                  padding="300"
                >
                  <Divider>
                    <Text.Paragraph as="span" color="tertiary">
                      Manually select queues
                    </Text.Paragraph>
                  </Divider>

                  <InputText
                    value={searchTerm}
                    onChange={handleSearch}
                    placeholder="Search..."
                    suffix={{
                      type: 'custom',
                      value: (
                        <FlexBox alignItems="center" padding="0 100">
                          <IconButton
                            variant="tertiary"
                            icon={searchTerm ? 'close' : 'search'}
                            onClick={searchTerm ? handleClearSearch : undefined}
                          />
                        </FlexBox>
                      ),
                      noWrap: true,
                    }}
                  />
                </Box>

                <SelectionList
                  className={styles.list}
                  isMultiple
                  selectedValues={selectedQueues}
                  onChange={handleSelectQueues}
                  defaultActiveItemIndex={0}
                >
                  {queues.map((queue) => (
                    <SelectionList.Item
                      key={queue.id}
                      value={queue.id}
                      className={styles.listItem}
                    >
                      {queue.name}
                    </SelectionList.Item>
                  ))}

                  {asyncStatus === 'pending' && (
                    <FlexBox as="li" padding="300" justifyContent="center">
                      <Spinner size={20} />
                    </FlexBox>
                  )}

                  {asyncStatus === 'fulfilled' && !queues.length && (
                    <FlexBox as="li" padding="300" justifyContent="center">
                      <EmptyState
                        title="No queues found"
                        description="Try changing your search criteria."
                      />
                    </FlexBox>
                  )}

                  {asyncStatus === 'fulfilled' && hasNextPage && (
                    <Waypoint onEnter={nextPage} />
                  )}
                </SelectionList>
              </>
            )}
          </div>
        }
        onToggle={handleToggle}
        buttonProps={{
          variant: 'secondary',
          icon:
            params.type === 'custom'
              ? 'users'
              : queueTypePresets[params.type ?? 'agent'].icon,
        }}
      >
        {label}
      </DropdownButton>
    );
  },
);

const queueTypePresets = {
  all: { label: 'All queues', icon: 'users' },
  agent: { label: 'Assigned', icon: 'user' },
  favourite: { label: 'Favorites', icon: 'star' },
  archived: { label: 'Archived', icon: 'archive' },
} as const satisfies Record<
  QueueDashboardQueueType,
  { label: string; icon?: IconNames }
>;
